'use client'

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { CATALOG_STALE_TIME } from '@/constants/catalog'
import { getSessionCustomerId } from '@/services/domain/auth/SessionService'
import {
    getCustomerRecommendationsService,
    getRelatedProductsService,
} from '@/services/domain/catalog/ProductRecommendationService'
import { useAuthStore } from '@/stores/useAuthStore'

/**
 * Recomendados para o cliente logado (PDP e carrinho). O id do cliente vem do token da sessão guardada (claim
 * `nameid`): o `customer` que os logins devolvem não tem id. Lido uma vez por token, não a cada render como as telas
 * legadas faziam com `jsonwebtoken`. Sem cliente, não busca.
 */
export function useCustomerRecommendations() {
    const token = useAuthStore((state) => state.data?.token)
    const customerId = useMemo(() => getSessionCustomerId(token), [token])

    const query = useQuery({
        queryKey: ['catalog-recommendations', 'customer', customerId],
        queryFn: () => getCustomerRecommendationsService(customerId as string),
        staleTime: CATALOG_STALE_TIME,
        enabled: Boolean(customerId),
    })

    return { products: query.data ?? [], isLoading: query.isLoading }
}

/** Relacionados a uma lista de produtos (carrinho sem login). Lista vazia não busca. */
export function useRelatedProducts(productIds: string[]) {
    const query = useQuery({
        queryKey: ['catalog-recommendations', 'products', productIds],
        queryFn: () => getRelatedProductsService(productIds),
        staleTime: CATALOG_STALE_TIME,
        enabled: productIds.length > 0,
    })

    return { products: query.data ?? [], isLoading: query.isLoading }
}
